import React, { FC, useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Combobox,
  Option,
  TableRow,
  TextInput,
  Table,
  Button,
  ButtonColor,
  ButtonSize,
} from '@code/innovance-react-ui-kit';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import FormModal, { ButtonRow } from 'src/front/components//FormModal';
import usePRM from 'src/front/common/hooks/usePRM';
import { ReferenceDataType, GlobalBonusUserInfoResponse } from 'src/front/api/generated';
import {
  Title,
  Subtitle,
  FormRow,
  Separator,
  FormElement,
  Form,
} from 'src/front/components/FormStyles';
import {
  formatNumeric,
  dictionaryLineFormat,
  optionFormat,
} from 'src/front/common/utils/formUtils';
import { GlobalBonus, LoanProductGlobalPage } from 'src/front/api/apiInstance';

interface Props {
  onClose: () => void;
}
interface GlobalBonusFormData {
  connection: Option | null;
  employeeNumber: string;
}
const defaultValues = {
  connection: null,
  employeeNumber: '',
};
const tableHeaders = ['general_components_employeeNumber_label', 'general_customerName_label'];
const GlobalBonusForm: FC<Props> = ({ onClose }) => {
  const connections = usePRM(ReferenceDataType.GLOBALBONUSCONNECTIONTYPE);
  const [employeeData, setEmployeeData] = useState<GlobalBonusUserInfoResponse>();
  const [selectedEmployee, setSelectedEmployee] = useState<TableRow | undefined>();
  const [isSearchSubmitted, setIsSearchSubmitted] = useState<boolean>(false);
  const { t } = useTranslation();
  const { errors, watch, handleSubmit, getValues, setValue, register } = useForm<
    GlobalBonusFormData
  >({
    defaultValues,
  });
  const registerFields = useCallback(() => {
    register('connection');
    register('employeeNumber');
  }, [register]);
  useEffect(() => {
    registerFields();
    const getData = async (): Promise<void> => {
      const { data } = await LoanProductGlobalPage.getLoanProductGlobalBonusPageData();
      if (data.result === 'OK' && data.pageData) {
        setValue('employeeNumber', data.pageData.employeeNumber);
        setValue('connection', optionFormat(data.pageData.connection));
      }
    };
    getData();
  }, [registerFields, setValue]);
  const handleComboChange = (name: string) => (option: Option | null) => {
    setValue(name, option, { shouldValidate: true, shouldDirty: true });
  };
  const handleSearch = async (): Promise<void> => {
    const { employeeNumber } = getValues();
    if (employeeNumber) {
      const { data } = await GlobalBonus.getGlobalBonusUserInfo({
        employeeNumber,
      });
      if (data.result === 'OK') {
        setEmployeeData(data);
        setIsSearchSubmitted(true);
      }
    }
  };
  const onSubmit = async (): Promise<void> => {
    const employeeNumber = selectedEmployee?.columns[0] || '';
    const { data } = await LoanProductGlobalPage.validateLoanProductGlobalBonusPageData({
      connection: dictionaryLineFormat(watch('connection')),
      employeeNumber,
    });
    if (data.result === 'OK') {
      onClose();
    }
  };
  const tableData: TableRow | undefined = employeeData?.fullName
    ? {
        id: '1',
        columns: [employeeData.employeeNumber || '', employeeData.fullName || ''],
      }
    : undefined;
  const ResetButton = styled(Button)`
    border: none;
  `;
  const handleReset = async (): Promise<void> => {
    setIsSearchSubmitted(false);
    setEmployeeData(undefined);
    setSelectedEmployee(undefined);
    setValue('connection', null);
    setValue('employeeNumber', '');
  };
  return (
    <FormModal onClose={onClose} size="600px" zIndex={200}>
      <Title>
        <Trans i18nKey="loanApp_globalBonus_globalBonus_title" />
      </Title>
      <Subtitle>
        <Trans i18nKey="loanApp_globalBonus_employeeInformation_subtitle" />
      </Subtitle>
      <Separator />
      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormRow>
          <FormElement>
            <Combobox
              errorMessage={t(errors?.connection?.message as string)}
              items={connections}
              label={t('general_components_connection_label')}
              name="connection"
              onChange={handleComboChange('connection')}
              value={watch('connection')}
            />
          </FormElement>
          <FormElement>
            <TextInput
              errorMessage={t(errors?.employeeNumber?.message as string)}
              formatter={formatNumeric}
              label={t('general_components_employeeNumber_label')}
              maxLength={60}
              name="employeeNumber"
              onChange={(value) => setValue('employeeNumber', value)}
              value={watch('employeeNumber')}
            />
          </FormElement>
        </FormRow>
        <Separator />
        <FormRow>
          <Button
            color={ButtonColor.Primary}
            onClick={handleSearch}
            size={ButtonSize.Medium}
            text={t('general_components_search_button')}
            type="button"
          />
        </FormRow>
        <Separator />
        {isSearchSubmitted ? (
          <>
            <Subtitle>
              <Trans i18nKey="loanApp_employeeList_subtitle" />
            </Subtitle>
            <FormRow>
              <Table
                emptyTableText={t('general_components_notFound_errorMessage')}
                headers={tableHeaders.map((head) => t(head))}
                items={tableData ? [tableData] : []}
                onSelect={setSelectedEmployee}
                pageNumber={1}
                selectedItem={selectedEmployee}
                widths={['45', '55']}
              />
            </FormRow>
            <Separator />
            <ButtonRow>
              <ResetButton
                color={ButtonColor.Secondary}
                onClick={handleReset}
                size={ButtonSize.Medium}
                text={t('general_components_reset_button')}
                type="button"
              />
              <Button
                color={ButtonColor.Primary}
                size={ButtonSize.Medium}
                text={t('general_save_button')}
                type="submit"
              />
            </ButtonRow>
          </>
        ) : null}
      </Form>
    </FormModal>
  );
};

export default GlobalBonusForm;
